const CHAT_ENDPOINT = '/assistant/chat/'

const GENERIC_ERROR = "Sorry, I couldn't answer that. Try again in a moment."
const OFFLINE_ERROR = "That didn't send — check your connection and try again."

// Matches MAX_QUESTION_LENGTH / MAX_HISTORY_TURNS on the server.
export const MAX_QUESTION_LENGTH = 500
export const MAX_HISTORY_TURNS = 3

/**
 * Ask the performance assistant a question.
 *
 * The server is stateless: the caller passes the last few exchanges so follow-ups
 * ("how many that week?") resolve. Only the most recent MAX_HISTORY_TURNS are sent,
 * since every extra turn is paid for on every question.
 *
 * The server's error strings are written for the user, so they are surfaced as-is.
 *
 * @param {string} question
 * @param {{question: string, answer: string}[]} [history] - Earlier exchanges, oldest first
 * @returns {Promise<{ok: boolean, answer?: string, error?: string}>} Never rejects
 */
export async function askAssistant(question, history = []) {
  try {
    // Imported here, not at module scope, for the same reason as services/bugReports.js:
    // AxiosClient -> store -> user services -> AxiosClient is a cycle when pulled in from App.vue.
    const { default: axiosInstance } = await import('./AxiosClient')
    const { data } = await axiosInstance.post(CHAT_ENDPOINT, {
      question,
      history: history.slice(-MAX_HISTORY_TURNS),
    })
    return data?.answer ? { ok: true, answer: data.answer } : { ok: false, error: GENERIC_ERROR }
  } catch (err) {
    if (err.response) {
      return { ok: false, error: err.response.data?.error || GENERIC_ERROR }
    }
    return { ok: false, error: OFFLINE_ERROR }
  }
}

// Mirrors server/nce_go/assistant/safety.py: 13-19 digits (spaces/dashes allowed) passing the Luhn check.
const CARD_CANDIDATE = /(?<!\d)(?:\d[ -]?){12,18}\d(?!\d)/g

export const CARD_NUMBER_ERROR =
  "For security, please don't enter card numbers or other payment details. " +
  'Remove the number and ask again - for a failed donation, use its pledge ID instead.'

function luhnValid(digits) {
  let total = 0
  for (let i = 0; i < digits.length; i++) {
    let value = Number(digits[digits.length - 1 - i])
    if (i % 2) {
      value *= 2
      if (value > 9) value -= 9
    }
    total += value
  }
  return total % 10 === 0
}

/**
 * True if the text contains something shaped like a payment card number. Checked before sending so
 * card data never leaves the browser; the server enforces the same rule.
 */
export function containsCardNumber(text) {
  for (const match of (text || '').matchAll(CARD_CANDIDATE)) {
    const digits = match[0].replace(/\D/g, '')
    if (
      digits.length >= 13 &&
      digits.length <= 19 &&
      new Set(digits).size > 1 &&
      luhnValid(digits)
    ) {
      return true
    }
  }
  return false
}

// Typed exactly, it's answered in the browser without a server call (the system prompt covers rewordings).
export const NAME_QUESTION = 'Why is your name Meredith?'
export const NAME_ANSWER =
  "I'm named after Meredith Garofalo, a legend in the field of face-to-face fundraising."

export const EXAMPLE_QUESTIONS = [
  'Which offices had the most signups last week?',
  'Which office had the highest PSA last month?',
  'What was the M1 rate for each office last month?',
  "Why can't I add a new fundraiser?",
]

export default {
  askAssistant,
  containsCardNumber,
  CARD_NUMBER_ERROR,
  EXAMPLE_QUESTIONS,
  MAX_QUESTION_LENGTH,
  MAX_HISTORY_TURNS,
  NAME_ANSWER,
  NAME_QUESTION,
}
